import { Button, LogoMark, TextField, Wordmark } from "@flexfreight/ui";
import { useQuery } from "@tanstack/react-query";
import { useState, type FormEvent, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import { ApiError } from "../api/client";
import { mfaApi, passwordApi, sendLoginCode, signIn, signinByCode, signOut, verifyCode, verifyLoginCode, type MfaMethod } from "./session";

export function AuthFrame({ title, lead, children }: { title: string; lead: ReactNode; children: ReactNode }) {
  return (
    <main className="ffa-auth">
      <div className="ffa-auth__card">
        <div className="ffa-auth__brand">
          <LogoMark className="ffa-auth__mark" title="" />
          <Wordmark />
          <span className="ffa-brand__tag">Admin</span>
        </div>
        <h1 className="ffa-auth__title">{title}</h1>
        <p className="ffa-auth__lead">{lead}</p>
        {children}
      </div>
    </main>
  );
}

function errorText(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 401) return e.message === "Invalid verification code" ? "That code didn't work. Try the newest one." : "Email or password is incorrect.";
    if (e.status === 400 && e.message === "Invalid verification code") return "That code didn't work. Try the newest one.";
    if (e.status === 429) return "Too many attempts. Wait a minute and try again.";
    return e.message;
  }
  return "Something went wrong. Try again.";
}

const CODE_METHOD_LABEL = { email: "Email me a sign-in code", sms: "Text me a sign-in code" } as const;
const EMAIL_OK = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Sign in with email + password, or (when the console has that provider connected and its switch on) with a
 * 6-digit code sent by email or text. The code options are only shown when the server offers them.
 */
export function SignIn({ onDone }: { onDone: () => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [codeStep, setCodeStep] = useState<{ method: "email" | "sms"; email: string } | null>(null);
  const [forgot, setForgot] = useState(false);
  const [params] = useSearchParams();
  const options = useQuery({ queryKey: ["auth", "signin-options"], queryFn: signinByCode.options, retry: false, staleTime: 60_000 });
  const offer = options.data ?? { email: false, sms: false };
  const emailOk = EMAIL_OK.test(email.trim());

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(email.trim(), password);
      onDone();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function sendCode(method: "email" | "sms") {
    setBusy(true);
    setError(null);
    try {
      await signinByCode.send(email.trim().toLowerCase(), method);
      setCodeStep({ method, email: email.trim().toLowerCase() });
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  if (codeStep) {
    return <SigninCodeStep method={codeStep.method} email={codeStep.email} offer={offer} onDone={onDone} onBack={() => { setCodeStep(null); setError(null); }} onSwitch={(m) => void sendCode(m)} />;
  }
  if (forgot) {
    return <ForgotPassword email={email} onBack={() => setForgot(false)} />;
  }

  const banner = params.get("invited") === "1" ? "Your staff account is ready. Sign in with the password you just set." : params.get("reset") === "1" ? "Password updated. Sign in with the new one." : null;
  return (
    <AuthFrame title="Sign in" lead="FlexFreight's staff tool. Use your FlexFreight staff account.">
      <form className="ffa-auth__form" onSubmit={submit} noValidate>
        {banner ? (
          <p className="ffa-auth__hint" role="status">
            {banner}
          </p>
        ) : null}
        <TextField label="Email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required />
        <TextField label="Password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        {error ? (
          <p className="ffa-auth__error" role="alert">
            {error}
          </p>
        ) : null}
        <Button type="submit" variant="primary" loading={busy && !codeStep} disabled={!email || !password}>
          Sign in
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={() => setForgot(true)}>
          Forgot your password?
        </Button>
        {offer.email || offer.sms ? (
          <div className="ffa-auth__alt" role="group" aria-label="Sign in with a code instead">
            <p className="ffa-auth__divider" aria-hidden="true">
              <span>or, with a code instead of a password</span>
            </p>
            {offer.email ? (
              <Button type="button" disabled={!emailOk || busy} onClick={() => void sendCode("email")}>
                {CODE_METHOD_LABEL.email}
              </Button>
            ) : null}
            {offer.sms ? (
              <Button type="button" disabled={!emailOk || busy} onClick={() => void sendCode("sms")}>
                {CODE_METHOD_LABEL.sms}
              </Button>
            ) : null}
            <p className="ffa-auth__hint">
              {emailOk ? "" : "Type your email first. "}
              {offer.sms ? "Texts go only to the verified mobile on your staff account; with none on file, use email." : null}
            </p>
          </div>
        ) : null}
      </form>
    </AuthFrame>
  );
}

/** Forgot password: the reset link for a staff account opens this console's /set-password page. Same answer for any address. */
function ForgotPassword({ email: initial, onBack }: { email: string; onBack: () => void }) {
  const [email, setEmail] = useState(initial);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await passwordApi.forgot(email.trim().toLowerCase());
      setSent(true);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <AuthFrame title="Reset your password" lead={sent ? "Check your inbox." : "Enter your staff email and we'll send a link to choose a new password."}>
      <form className="ffa-auth__form" onSubmit={submit} noValidate>
        {sent ? (
          <p className="ffa-auth__hint" role="status">
            If a staff account exists for that email, a reset link is on its way. It expires in 30 minutes.
          </p>
        ) : (
          <TextField label="Email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required />
        )}
        {error ? (
          <p className="ffa-auth__error" role="alert">
            {error}
          </p>
        ) : null}
        {sent ? null : (
          <Button type="submit" variant="primary" loading={busy} disabled={!EMAIL_OK.test(email.trim())}>
            Send reset link
          </Button>
        )}
        <Button type="button" variant="ghost" onClick={onBack}>
          Back to sign in
        </Button>
      </form>
    </AuthFrame>
  );
}

/** Second screen of sign-in by code: enter the 6 digits. The wording never says whether the account exists. */
function SigninCodeStep({ method, email, offer, onDone, onBack, onSwitch }: { method: "email" | "sms"; email: string; offer: { email: boolean; sms: boolean }; onDone: () => void; onBack: () => void; onSwitch: (m: "email" | "sms") => void }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const digits = code.replace(/\s+/g, "");

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signinByCode.verify(email, digits);
      onDone();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }
  async function resend() {
    setResending(true);
    setError(null);
    setNotice(null);
    try {
      await signinByCode.send(email, method);
      setCode("");
      setNotice("If that account exists, we sent a new code.");
    } catch (err) {
      setError(errorText(err));
    } finally {
      setResending(false);
    }
  }

  const lead =
    method === "email"
      ? `If ${email} is a staff account, we emailed it a 6-digit code. It expires in 10 minutes.`
      : `If ${email} is a staff account with a verified mobile on file, we texted that number a 6-digit code. It expires in 10 minutes. No mobile on file? Ask for the code by email instead.`;
  return (
    <AuthFrame title="Enter your code" lead={lead}>
      <form className="ffa-auth__form" onSubmit={submit} noValidate>
        <TextField label="Sign-in code" inputMode="numeric" autoComplete="one-time-code" maxLength={7} value={code} onChange={(e) => setCode(e.target.value)} required autoFocus />
        {notice ? <p className="ffa-auth__hint" role="status">{notice}</p> : null}
        {error ? (
          <p className="ffa-auth__error" role="alert">
            {error}
          </p>
        ) : null}
        <Button type="submit" variant="primary" loading={busy} disabled={digits.length !== 6}>
          Sign in
        </Button>
        <Button type="button" loading={resending} onClick={() => void resend()}>
          {method === "email" ? "Send a new code by email" : "Send a new code by text"}
        </Button>
        {method === "sms" && offer.email ? (
          <Button type="button" variant="ghost" onClick={() => onSwitch("email")}>
            Email me a code instead
          </Button>
        ) : null}
        <Button type="button" variant="ghost" onClick={onBack}>
          Use a password instead
        </Button>
      </form>
    </AuthFrame>
  );
}

const CHANNEL_LABEL = { email: "email", sms: "text" } as const;

/** Second step: authenticator code by default when it's on, or a code sent by email or text. */
export function VerifyCode({ onDone, methods = [{ method: "totp", available: true, hint: null }], help = null }: { onDone: () => void; methods?: MfaMethod[]; help?: string | null }) {
  const hasTotp = methods.some((m) => m.method === "totp");
  const senders = methods.filter((m): m is MfaMethod & { method: "email" | "sms" } => m.method !== "totp");
  const [via, setVia] = useState<"totp" | { channel: "email" | "sms"; hint: string } | null>(hasTotp ? "totp" : null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function send(channel: "email" | "sms") {
    setSending(channel);
    setError(null);
    try {
      const r = await sendLoginCode(channel);
      setVia({ channel, hint: r.hint });
      setCode("");
    } catch (err) {
      setError(errorText(err));
    } finally {
      setSending(null);
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!via) return;
    setBusy(true);
    setError(null);
    try {
      const c = code.replace(/\s+/g, "");
      if (via === "totp") await verifyCode(c);
      else await verifyLoginCode(c);
      onDone();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  const lead = via === "totp" ? "Enter the 6-digit code from your authenticator app." : via ? `We sent a 6-digit code to ${via.hint}. It expires in 10 minutes.` : "Choose where to send a 6-digit code.";
  return (
    <AuthFrame title="Two-factor check" lead={lead}>
      <form className="ffa-auth__form" onSubmit={submit} noValidate>
        {via ? (
          <TextField label="Verification code" inputMode="numeric" autoComplete="one-time-code" maxLength={7} value={code} onChange={(e) => setCode(e.target.value)} required />
        ) : null}
        {help ? <p className="ffa-auth__error" role="alert">{help}</p> : null}
        {error ? (
          <p className="ffa-auth__error" role="alert">
            {error}
          </p>
        ) : null}
        {via ? (
          <Button type="submit" variant="primary" loading={busy} disabled={code.replace(/\s+/g, "").length !== 6}>
            Verify
          </Button>
        ) : null}
        {senders.map((m) => (
          <Button key={m.method} disabled={!m.available} loading={sending === m.method} onClick={() => void send(m.method)}>
            {typeof via === "object" && via?.channel === m.method ? `Send a new code by ${CHANNEL_LABEL[m.method]}` : `Send code by ${CHANNEL_LABEL[m.method]}${m.hint ? ` (${m.hint})` : ""}`}
          </Button>
        ))}
        {hasTotp && via !== "totp" ? (
          <Button variant="ghost" onClick={() => { setVia("totp"); setCode(""); setError(null); }}>
            Use my authenticator app
          </Button>
        ) : null}
        <Button variant="ghost" onClick={() => void signOut().finally(onDone)}>
          Use a different account
        </Button>
      </form>
    </AuthFrame>
  );
}

const PORTAL_URL = "https://app.flexfreight.net";

export function StaffOnly({ email, onDone }: { email: string; onDone: () => void }) {
  return (
    <AuthFrame title="This is FlexFreight's staff tool" lead={<>{email ? <>You're signed in as {email}, which isn't a staff account. </> : null}Customers manage freight in the FlexFreight portal.</>}>
      <div className="ffa-auth__form">
        <a className="ffx-btn ffx-btn--primary" href={PORTAL_URL}>
          Go to the customer portal
        </a>
        <Button variant="ghost" onClick={() => void signOut().finally(onDone)}>
          Sign out
        </Button>
      </div>
    </AuthFrame>
  );
}

export function MfaRequired({ email, onDone }: { email: string; onDone: () => void }) {
  return (
    <AuthFrame
      title="Turn on two-factor sign-in"
      lead={
        <>
          Owner and admin accounts need an authenticator app or text-message codes for the admin console. Email codes don&apos;t count here, because the same inbox gets
          password resets. Already set one up? Sign out and sign in with it. Otherwise add text codes below, or an authenticator for {email || "your account"} in the customer portal.
        </>
      }
    >
      <QuickEnroll onDone={onDone} />
      <div className="ffa-auth__form">
        <a className="ffx-btn" href={`${PORTAL_URL}/settings#set-security`}>
          Set up an authenticator app
        </a>
        <Button variant="ghost" onClick={() => void signOut().finally(onDone)}>
          Sign out
        </Button>
      </div>
    </AuthFrame>
  );
}

/** Text codes set up right on the console gate: number + password, send a code, enter it, done. (No email: see above.) */
export function QuickEnroll({ onDone }: { onDone: () => void }) {
  const q = useQuery({ queryKey: ["auth", "mfa"], queryFn: mfaApi.status, retry: false });
  const [hint, setHint] = useState<string | null>(null);
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const s = q.data;
  if (!s || !s.sms.available || s.sms.enabled) return null;

  async function start() {
    setBusy(true);
    setError(null);
    try {
      const r = await mfaApi.startSms(phone.trim(), password);
      setHint(r.hint);
      setPassword("");
      setCode("");
    } catch (err) {
      setError(err instanceof ApiError && err.status === 401 ? "That password isn't right." : errorText(err));
    } finally {
      setBusy(false);
    }
  }
  async function confirm(e: FormEvent) {
    e.preventDefault();
    if (!hint) return void start();
    setBusy(true);
    setError(null);
    try {
      await mfaApi.enable("sms", code.replace(/\s+/g, ""));
      onDone();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="ffa-auth__form" onSubmit={confirm} noValidate aria-label="Set up text codes">
      {hint ? (
        <>
          <p className="ffa-auth__lead">We texted a 6-digit code to {hint}.</p>
          <TextField label="Code" inputMode="numeric" autoComplete="one-time-code" maxLength={7} value={code} onChange={(e) => setCode(e.target.value)} required />
        </>
      ) : (
        <>
          <TextField label="Mobile number" type="tel" autoComplete="tel" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} hint="US numbers can skip the +1" />
          <TextField label="Password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </>
      )}
      {error ? <p className="ffa-auth__error" role="alert">{error}</p> : null}
      {hint ? (
        <>
          <Button type="submit" variant="primary" loading={busy} disabled={code.replace(/\s+/g, "").length !== 6}>
            Turn on
          </Button>
          <Button variant="ghost" onClick={() => setHint(null)}>
            Back
          </Button>
        </>
      ) : (
        <Button type="submit" variant="primary" loading={busy} disabled={phone.replace(/\D/g, "").length < 10 || !password}>
          Text me a code
        </Button>
      )}
    </form>
  );
}
